import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Loader from './Loader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 400px;
  max-width: 90%;
  padding: 30px 25px;
  box-shadow: 0 4px 20px rgba(35,62,88,0.2);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #21325b;
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 1rem;
  margin-bottom: 5px;
  color: #182237;
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  font-size: 1rem;
  resize: vertical;
  min-height: 80px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  color: #fff;
  background: ${props => props.variant === 'cancel' ? '#d24949' : '#426dff'};
  &:hover {
    background: ${props => props.variant === 'cancel' ? '#b33b3b' : '#305de2'};
  }
`;

// Add or Edit Inventory Product Category Modal
export default function AddInventoryCategoryModal({
  isOpen,
  onClose,
  onCategoryAdded,
  isEdit = false,
  editCategory = null,
  onCategoryUpdated
}) {
  const [formData, setFormData] = useState({
    category_name: '',
    category_description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Initialize form data on edit or reset on add/close
  useEffect(() => {
    if (isEdit && editCategory) {
      setFormData({
        category_name: editCategory.category_name || '',
        category_description: editCategory.category_description || ''
      });
      setError('');
    } else {
      setFormData({ category_name: '', category_description: '' });
      setError('');
    }
    setFieldErrors({});
    setTouched({});
    setSubmitted(false);
  }, [isEdit, editCategory, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));

    const errors = validateForm({ ...formData, [name]: value });
    setFieldErrors(prev => ({ ...prev, [name]: errors[name] }));
  };

  const validateForm = (fields = formData) => {
    const errors = {};
    const nameRegex = /^[a-zA-Z0-9\s\-'&]{3,50}$/; // Allows letters, numbers, spaces, dash, apostrophe, ampersand, length 3-50
    const descRegex = /^[a-zA-Z0-9\s,\.'\-()&]{0,200}$/; // Allows letters, numbers, common punctuations, max 200 chars

    if (!fields.category_name.trim()) {
      errors.category_name = 'Category Name is required';
    } else if (!nameRegex.test(fields.category_name)) {
      errors.category_name = '3-50 chars; letters, numbers, space, -, \', & allowed';
    }

    if (fields.category_description && !descRegex.test(fields.category_description)) {
      errors.category_description = 'Up to 200 chars; letters, numbers, commas, dots, parentheses, &, -, \' allowed';
    }

    return errors;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSubmitted(true);

    const errors = validateForm(formData);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      if (isEdit && editCategory) {
        // Update category
        const res = await axios.post(`${BACKEND_URL}/categories/edit/${editCategory.id}`, formData, { headers });
        if (res.status === 200) {
          onCategoryUpdated && onCategoryUpdated();
          onClose();
        }
      } else {
        // Add new category
        const res = await axios.post(`${BACKEND_URL}/categories`, formData, { headers });
        if (res.status === 201) {
          setFormData({ category_name: '', category_description: '' });
          onCategoryAdded && onCategoryAdded();
          onClose();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      {loading && <Loader />}
      <ModalContainer>
        <ModalHeader>{isEdit ? 'Edit Product Category' : 'Add New Product Category'}</ModalHeader>
        <ModalField>
          <Label>
            Category Name<span style={{ color: 'red', marginLeft: '4px' }}>*</span>
          </Label>
          <Input
            name="category_name"
            value={formData.category_name}
            onChange={handleChange}
            autoFocus
          />
          {(touched.category_name || submitted) && fieldErrors.category_name && (
            <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.category_name}</span>
          )}
        </ModalField>

        <ModalField>
          <Label>Category Description</Label>
          <TextArea
            name="category_description"
            value={formData.category_description}
            onChange={handleChange}
          />
        </ModalField>

        {error && <div style={{ color: '#d24949', marginBottom: 10 }}>{error}</div>}

        <Label style={{ marginTop: '40px' }}>
          <span style={{ color: 'red', marginLeft: '4px' }}>*</span> marked fields are required.
        </Label>

        <ButtonRow>
          <Button variant="cancel" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Category' : 'Add Category')}
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
